import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { TrendingUp, TrendingDown, Newspaper, RefreshCw, Landmark, Building2, FileText, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { MOCK_FOREX_PAIRS } from "@/data/mockData";
import { useHighImpactArticles } from "@/hooks/useArticles";

const HERO_STATS = [
  { label: "Forex Brokers", value: "6,000+", icon: Landmark, accent: "from-primary to-primary/60" },
  { label: "Prop Firms", value: "2,500+", icon: Building2, accent: "from-secondary to-secondary/60" },
  { label: "Daily Articles", value: "50+", icon: FileText, accent: "from-wire-gold to-wire-gold/60" },
];

const HeroSection = () => {
  const [activeHeadline, setActiveHeadline] = useState(0);
  const [activePairIndex, setActivePairIndex] = useState(0);
  const { data: topHeadlines = [] } = useHighImpactArticles(4);

  useEffect(() => {
    if (topHeadlines.length === 0) return;
    const timer = setInterval(() => {
      setActiveHeadline((prev) => (prev + 1) % topHeadlines.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [topHeadlines.length]);

  const pairsPerPage = 4;
  const totalPages = Math.ceil(MOCK_FOREX_PAIRS.length / pairsPerPage);

  useEffect(() => {
    if (totalPages <= 1) return;
    const timer = setInterval(() => {
      setActivePairIndex((prev) => (prev + 1) % totalPages);
    }, 30000);
    return () => clearInterval(timer);
  }, [totalPages]);

  return (
    <section className="relative overflow-hidden bg-gradient-hero">
      {/* Decorative orbs - hidden on mobile for perf */}
      <div className="absolute inset-0 bg-grid-pattern opacity-20 hidden md:block" />
      <div className="hidden md:block absolute top-10 left-[10%] h-72 w-72 rounded-full bg-primary/8 blur-[120px]" />
      <div className="hidden md:block absolute bottom-0 right-[5%] h-56 w-56 rounded-full bg-secondary/8 blur-[100px]" />

      <div className="container relative z-10 py-8 md:py-14">
        {/* Forex rates bar */}
        <div className="mb-8 rounded-xl glass px-4 py-2.5">
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 flex-1 justify-center">
              {MOCK_FOREX_PAIRS.slice(activePairIndex * pairsPerPage, activePairIndex * pairsPerPage + pairsPerPage).map((pair) => (
                <div key={pair.pair} className="inline-flex items-center gap-3">
                  <span className="font-display text-sm font-semibold text-foreground">{pair.pair}</span>
                  <span className="text-sm text-muted-foreground font-mono">{pair.price}</span>
                  <span className={`flex items-center gap-0.5 text-xs font-semibold ${pair.direction === "up" ? "text-primary" : "text-destructive"}`}>
                    {pair.direction === "up" ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {pair.change}
                  </span>
                </div>
              ))}
            </div>
            <button
              onClick={() => setActivePairIndex((prev) => (prev + 1) % totalPages)}
              className="shrink-0 ml-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors"
              aria-label="Next forex pairs"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Main hero content - no motion wrappers, use CSS animation */}
        <div className="text-center mb-10 animate-fade-in">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-xs text-muted-foreground mb-5">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse-dot" />
            Live Market Intelligence
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight mb-3">
            Your Complete Pulse of{" "}
            <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Forex, Fintech & Prop Trading
            </span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Trusted by traders tracking 6,000+ brokers and 2,500+ prop firms worldwide
          </p>

          {/* Stats row */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            {HERO_STATS.map((stat) => {
              const Icon = stat.icon;
              return (
                <div
                  key={stat.label}
                  className="group relative w-full sm:w-52 rounded-xl glass p-5 text-center transition-transform duration-200 hover:-translate-y-1"
                >
                  <div className={`mx-auto mb-2 h-10 w-10 rounded-lg bg-gradient-to-br ${stat.accent} flex items-center justify-center`}>
                    <Icon className="h-5 w-5 text-background" />
                  </div>
                  <div className="font-display text-2xl font-bold text-foreground">{stat.value}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{stat.label}</div>
                </div>
              );
            })}
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="#wire"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-display font-semibold text-sm bg-primary text-primary-foreground hover:bg-primary/85 transition-colors duration-200"
            >
              Read Latest Articles
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-display font-semibold text-sm border border-secondary text-foreground hover:bg-secondary hover:text-white transition-colors duration-200"
            >
              About Us
            </Link>
          </div>
        </div>

        {/* Top Stories - keep AnimatePresence only for headline rotation */}
        {topHeadlines.length > 0 && (
          <div className="max-w-3xl mx-auto animate-fade-in" style={{ animationDelay: '150ms', animationFillMode: 'both' }}>
            <div className="rounded-xl glass p-5">
              <div className="flex items-center gap-2 mb-4">
                <Newspaper className="h-4 w-4 text-primary" />
                <h2 className="font-display text-sm font-semibold">Top Stories</h2>
                <span className="h-1.5 w-1.5 rounded-full bg-destructive animate-pulse-dot ml-auto" />
                <span className="text-xs text-muted-foreground">Live</span>
              </div>

              <Link to={`/news/${topHeadlines[activeHeadline]?.slug}`} className="block relative min-h-[72px] mb-4 group/hero">
                <AnimatePresence mode="wait">
                  <motion.div key={activeHeadline} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                    <h3 className="font-display text-lg font-semibold leading-tight mb-2 text-foreground group-hover/hero:text-primary transition-colors">
                      {topHeadlines[activeHeadline]?.headline}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{topHeadlines[activeHeadline]?.excerpt}</p>
                  </motion.div>
                </AnimatePresence>
              </Link>

              <div className="flex items-center gap-3 mb-3">
                {topHeadlines.map((_, i) => (
                  <button key={i} onClick={() => setActiveHeadline(i)} aria-label={`Go to headline ${i + 1}`} className="h-8 w-8 flex items-center justify-center rounded-full transition-all duration-300"><span className={`block rounded-full transition-all duration-300 ${i === activeHeadline ? "w-6 h-1.5 bg-primary" : "w-1.5 h-1.5 bg-muted-foreground/30"}`} /></button>
                ))}
              </div>

              <div className="border-t border-border/50 pt-3 space-y-1.5">
                {topHeadlines.map((article, i) => (
                  <Link
                    key={article.id}
                    to={`/news/${article.slug}`}
                    className={`block w-full text-left text-sm px-3 py-2 rounded-md transition-colors ${i === activeHeadline ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:text-foreground hover:bg-accent/50"}`}
                  >
                    <span className="line-clamp-1">{article.headline}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroSection;
